'use client';

import React from 'react';
import { ExperienceType } from '../types';
import PickupPoint from '@/components/packages/PickupPoint';
import { ActivityWithDay, Location } from '@/types/itineraryTypes';

interface ExperiencePickupPointProps {
  experience: unknown;
  type: ExperienceType;
}

export const ExperiencePickupPoint: React.FC<ExperiencePickupPointProps> = ({
  experience,
  type,
}) => {
  if (!experience || type !== 'activity') return null;

  const exp = experience as {
    pickupLocation?: {
      _id?: string;
      name?: string;
      coordinates?: {
        type?: string;
        coordinates?: [number, number];
      };
      pincode?: string;
      district?: string;
      state?: string;
      city?: string;
      country?: string;
      place_id?: string;
      full_address?: string;
      imageUrl?: string;
      description?: string;
      type?: string;
      isBusiness?: boolean;
      isActive?: boolean;
      isDeleted?: boolean;
      cost?: string | number | null;
      rating?: number;
      reviews?: number;
      isRegion?: boolean;
      createdAt?: string;
      updatedAt?: string;
      __v?: number;
    };
    activities?: Array<{
      time?: number | string;
      startTime?: string;
      endTime?: string;
      title?: string;
      description?: string;
      cost?: string | number;
      icon?: string;
      day?: string;
      imageUrls?: string[];
      notes?: unknown[];
      targetCurrencyRate?: string | number;
      locations?: {
        pincode?: string;
        name?: string;
        description?: string;
        type?: string;
        isBusiness?: boolean;
        isRegion?: boolean;
        cost?: string | number;
        district?: string;
        city?: string;
        state?: string;
        rating?: string | number;
        reviews?: string | number;
        coordinates?: {
          lat?: number;
          lng?: number;
          coordinates?: [number, number];
        };
        nearByPublicTravels?: unknown;
        attractions?: unknown;
      };
    }>;
  };

  // Only show if pickupLocation exists
  if (!exp.pickupLocation) {
    return null;
  }

  const locationData = exp.pickupLocation;

  // Create ActivityWithDay format using pickupLocation
  const activityWithDay: ActivityWithDay = {
    time: '', // No time for pickup location
    title: locationData.name || 'Pickup Location',
    description: locationData.description || locationData.full_address || '',
    cost: '', // No cost for pickup location
    icon: '', // No icon for pickup location
    day: '1', // Default day
    locations: [
      {
        imageUrls: locationData.imageUrl ? [locationData.imageUrl] : [], // Use pickup location image if available
        pincode: locationData.pincode || '',
        name: locationData.name || '',
        description: locationData.description || '',
        type: locationData.type || '',
        isBusiness: !!locationData.isBusiness,
        isRegion: !!locationData.isRegion,
        cost: String(locationData.cost || ''),
        district: locationData.district || '',
        city: locationData.city || '',
        state: locationData.state || '',
        rating: String(locationData.rating ?? ''),
        reviews: String(locationData.reviews ?? ''),
        coordinates: (() => {
          const coords = locationData.coordinates;
          if (
            coords?.coordinates &&
            Array.isArray(coords.coordinates) &&
            coords.coordinates.length >= 2
          ) {
            const [lng, lat] = coords.coordinates;
            return { lat: Number(lat) || 0, lng: Number(lng) || 0 };
          }
          // Fallback for old coordinate format
          if (
            typeof coords === 'object' &&
            'lat' in coords &&
            'lng' in coords
          ) {
            return {
              lat: Number(coords.lat) || 0,
              lng: Number(coords.lng) || 0,
            };
          }
          return { lat: 0, lng: 0 };
        })(),
        nearByPublicTravels: undefined,
        attractions: undefined,
      } as Location,
    ],
  };

  return <PickupPoint activity={activityWithDay} />;
};
